import React, { useState, useRef } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { XCircle, Loader2, Camera, X as XIcon, Plus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const QUICK_REASONS = [
  'Draps non changés',
  'Sol sale',
  'Salle de bain non nettoyée',
  'Cuisine non nettoyée',
  'Poubelles pleines',
  'Lits non faits',
  'Linge oublié',
  'Terrasse non nettoyée',
  'Vitres sales',
  'Odeur persistante',
  "Oubli d'un consommable",
  'Dégât constaté',
];

const MAX_PHOTOS = 10;

/**
 * Dialog for admin/tech to reject a completed task.
 * Supports: quick-reason chips, free text, and up to 10 photos justifying the refusal.
 */
const RejectDialog = ({
  open,
  onOpenChange,
  rejectReason,
  setRejectReason,
  onReject,
  actionLoading,
  photos,
  setPhotos,
}) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const toggleReason = (reason) => {
    const current = (rejectReason || '').trim();
    const parts = current ? current.split(/\s*[•\n]\s*/).filter(Boolean) : [];
    if (parts.includes(reason)) {
      setRejectReason(parts.filter((p) => p !== reason).join(' • '));
    } else {
      setRejectReason([...parts, reason].join(' • '));
    }
  };

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const remaining = MAX_PHOTOS - (photos?.length || 0);
    if (remaining <= 0) {
      toast.error(`Maximum ${MAX_PHOTOS} photos atteint`);
      return;
    }
    const toUpload = list.slice(0, remaining);
    setUploading(true);
    const token = localStorage.getItem('token');
    const uploaded = [];
    for (const file of toUpload) {
      if (!file.type.startsWith('image/')) continue;
      const form = new FormData();
      form.append('file', file);
      try {
        const res = await axios.post(`${API_URL}/api/rejections/upload-photo`, form, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        });
        if (res.data?.path) uploaded.push(res.data.path);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Erreur lors de l'upload");
      }
    }
    if (uploaded.length) setPhotos([...(photos || []), ...uploaded]);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePhoto = (idx) => {
    setPhotos((photos || []).filter((_, i) => i !== idx));
  };

  const photoUrl = (p) => {
    if (!p) return '';
    if (p.startsWith('http')) return p;
    const token = localStorage.getItem('token');
    return `${API_URL}/api/photo?path=${encodeURIComponent(p)}&auth=${token}`;
  };

  const selectedReasons = (rejectReason || '').split(/\s*•\s*/).map((s) => s.trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <XCircle className="h-5 w-5" />
            Refuser la prestation
          </DialogTitle>
        </DialogHeader>

        <div className="py-2 space-y-4">
          <p className="text-sm text-gray-600">
            La femme de ménage sera notifiée par email avec le motif et les photos. Le paiement sera annulé.
          </p>

          {/* Motifs rapides */}
          <div className="space-y-2">
            <Label>Motifs rapides</Label>
            <div className="flex flex-wrap gap-2" data-testid="reject-quick-reasons">
              {QUICK_REASONS.map((r) => {
                const active = selectedReasons.includes(r);
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => toggleReason(r)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      active
                        ? 'bg-red-600 border-red-600 text-white'
                        : 'bg-white border-slate-300 text-slate-700 hover:border-red-400 hover:text-red-700'
                    }`}
                    data-testid={`quick-reason-${r.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {r}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Champ libre */}
          <div className="space-y-2">
            <Label>Détails / raison *</Label>
            <Textarea
              placeholder="Décrivez ce qui n'allait pas..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              className="resize-none"
              data-testid="reject-reason-input"
            />
          </div>

          {/* Photos */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Photos justificatives ({(photos || []).length}/{MAX_PHOTOS})</Label>
              {uploading && <Loader2 className="h-4 w-4 animate-spin text-slate-500" />}
            </div>

            <div className="grid grid-cols-4 gap-2">
              {(photos || []).map((p, idx) => (
                <div
                  key={`${p}-${idx}`}
                  className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 group"
                  data-testid={`reject-photo-${idx}`}
                >
                  <img src={photoUrl(p)} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePhoto(idx)}
                    className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`remove-reject-photo-${idx}`}
                  >
                    <XIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}

              {(photos || []).length < MAX_PHOTOS && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="aspect-square rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-1 text-slate-500 hover:text-red-600 hover:border-red-400 transition-colors text-xs"
                  data-testid="add-reject-photo-btn"
                >
                  <Plus className="h-5 w-5" />
                  <span>Ajouter</span>
                </button>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
              data-testid="reject-photo-input"
            />

            <p className="text-xs text-slate-500 flex items-center gap-1">
              <Camera className="h-3 w-3" />
              Les photos aident la FDM à comprendre le problème.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            className="bg-red-600 hover:bg-red-700"
            onClick={onReject}
            disabled={actionLoading || uploading || !rejectReason.trim()}
            data-testid="confirm-reject-btn"
          >
            {actionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Refuser la prestation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RejectDialog;
